import { useRef } from "react";
import { Text, useBreakpointValue } from "@chakra-ui/react";
import useSWR from "swr";
import CompanyFundingCard from "components/company/company-funding/company-funding-card";
import LimitWarning from "components/profile/limit-warning";
import ProfileBody from "components/profile/profile-body";
import useComponentSize from "hooks/use-component-size";
import useUser from "hooks/use-user";
import fetcher from "utils/fetcher";
import type { CompanyTags } from "data/company-tags";
import type { CompanyOverviewResponse } from "types/api/company/details/overview";
import TextLink from "components/common/link/text-link";
import TrialBanner from "components/common/trial-banner";
import usePermission from "hooks/use-permission";
import AboutCard from "./about-card";
import ClassificationCard from "./classification-card";
import CompanyHqCard from "./company-hq-card";
import ContactsCard from "./contacts-card";
import DescriptionCard from "./description-card";
import EmployeesRevenueCard from "./employees-revenue-card";
import OverviewBanner from "./overview-banner";
import SubsidiariesCard from "./subsidiaries-card";
import SummaryCardRow from "./summary-card-row";

interface CompanyOverviewProps {
  companySlug: string;
  isInternational: boolean;
  tags: CompanyTags;
}

const CompanyOverview = ({
  companySlug,
  isInternational = false,
  tags,
}: CompanyOverviewProps) => {
  const user = useUser();
  const isPublic = !user?.isLoggedIn || false;
  const isTrialing = user?.details?.isTrialing || false;

  const hasOverviewMapPermission = usePermission("companyOverviewMap");

  // calculate the max height of the Contacts table based on the height of the about content
  const aboutContainer = useRef();
  const { height: aboutHeight } = useComponentSize(aboutContainer);
  const hasTwoColumns = useBreakpointValue([false, false, true]);
  const maxContactsHeight = hasTwoColumns ? Math.max(aboutHeight, 316) : 316;

  const { data: companyData, error } = useSWR<CompanyOverviewResponse>(
    `/system/company/details/${companySlug}/overview`,
    fetcher, {
      revalidateOnFocus: false
    }
  );

  const isIntegrated = tags.some(({ id }) => id === 10);

  const isLoaded = !!companyData;

  const { hasChartData, chartData, companyProfile } = companyData || {};

  if (error?.info?.message === "You have reached your company view limit") {
    return <LimitWarning />;
  }

  return (
    <>
      {isTrialing && (
        <TrialBanner borderTopWidth="1px">
          <Text>
            During your trial, searches are limited to 50 results and company views are limited to 20. <TextLink href="/account#end-trial">Upgrade for full access.</TextLink>
          </Text>
        </TrialBanner>
      )}

      <OverviewBanner
        isLoaded={isLoaded}
        companyProfile={companyProfile}
        isInternational={isInternational}
        tags={tags}
        slug={companySlug}
      />

      <ProfileBody>
        <SummaryCardRow companyProfile={companyProfile} isLoaded={isLoaded} />

        <DescriptionCard isLoaded={isLoaded} companyProfile={companyProfile} />

        <ClassificationCard
          isLoaded={isLoaded}
          companyProfile={companyProfile}
        />

        <AboutCard
          isLoaded={isLoaded}
          companyProfile={companyProfile}
          ref={aboutContainer}
          isIntegrated={isIntegrated}
        />

        <ContactsCard
          isLoaded={isLoaded}
          isInternational={isInternational}
          maxContactsHeight={maxContactsHeight}
          companyProfile={companyProfile}
          aboutHeight={aboutHeight}
        />

        <CompanyHqCard isLoaded={isLoaded} hasOverviewMap={hasOverviewMapPermission} companyProfile={companyProfile} />

        <EmployeesRevenueCard
          isLoaded={isLoaded}
          chartData={chartData}
          hasChartData={hasChartData}
          isInternational={isInternational}
        />

        {isLoaded && isPublic && !!companyProfile?.latestCompanyRound && (
          <CompanyFundingCard
            title="Latest Funding Round — "
            round={companyProfile?.latestCompanyRound}
          />
        )}

        <SubsidiariesCard
          isLoaded={isLoaded}
          subsidiaries={companyProfile?.subsidiaries}
        />

      </ProfileBody>
    </>
  );
};

export default CompanyOverview;
